import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Filter } from "lucide-react";
import { Card, EmptyState } from "../common/Primitives";

/**
 * CuratedMatchesSection
 * ---------------------
 * Investor-facing curated deal-flow tiles with quick sector filter chips.
 */
export default function CuratedMatchesSection({ matches }) {
  const [activeSector, setActiveSector] = useState("All");

  // Hide matches whose startup document is missing a name — otherwise the grid
  // shows blank ghost tiles when the backend has orphan startup references.
  const cleanMatches = useMemo(
    () => (matches || []).filter((m) => m.startup_name && m.startup_name !== "Startup"),
    [matches]
  );

  const sectors = useMemo(() => {
    const set = new Set(["All"]);
    cleanMatches.forEach((m) => { if (m.sector) set.add(m.sector); });
    return Array.from(set).slice(0, 8);
  }, [cleanMatches]);

  const filtered = useMemo(() => {
    if (cleanMatches.length === 0) return [];
    if (activeSector === "All") return cleanMatches;
    return cleanMatches.filter((m) => m.sector === activeSector);
  }, [cleanMatches, activeSector]);

  return (
    <Card testId="section-curated-matches">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1.5"><Sparkles size={12} className="text-gold" /> Curated Matches</div>
          <div className="font-heading text-ink text-lg mt-1">Handpicked for your thesis</div>
        </div>
        <Link to="/investor/marketplace" className="text-emerald-700 text-sm font-heading hover:underline" data-testid="curated-matches-see-all">
          See marketplace →
        </Link>
      </div>

      {sectors.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="curated-matches-filters">
          <Filter size={12} className="text-ink-muted" />
          {sectors.map((s) => (
            <button
              key={s}
              onClick={() => setActiveSector(s)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                activeSector === s
                  ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                  : "border-navy/15 text-ink-muted hover:border-emerald-300"
              }`}
              data-testid={`curated-matches-filter-${s.toUpperCase()}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title="No curated matches yet"
          hint="Complete your preferences to unlock deal flow."
          action={
            <Link to="/investor/preferences" className="text-emerald-700 text-sm font-heading">
              Complete preferences →
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.slice(0, 6).map((m) => (
            <Link
              to={`/startup/${m.startup_id}`}
              key={m.startup_id}
              className={`relative border p-4 rounded-md transition-all group ${
                m.handpicked_by_venturaz
                  ? "border-gold/40 bg-gold/[0.04] hover:border-gold shadow-[0_0_0_1px_rgba(138,102,0,0.12)]"
                  : "border-white/10 hover:border-emerald-300"
              }`}
              data-testid={`curated-match-${m.startup_id}`}
            >
              {m.handpicked_by_venturaz && (
                <div
                  className="absolute -top-2 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white text-[9px] font-heading font-semibold uppercase tracking-widest shadow-sm"
                  data-testid={`curated-match-handpicked-${m.startup_id}`}
                >
                  <Sparkles size={9} /> VenturaZ Handpicked
                </div>
              )}
              <div className="flex justify-between items-start mb-2">
                <div className="min-w-0 flex-1">
                  <div className="text-ink font-heading truncate">{m.startup_name}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-1">
                    {m.sector || "—"} · {m.business_stage || "—"}
                  </div>
                </div>
                <div className="match-badge px-2 py-0.5 text-[11px] font-mono flex-shrink-0" data-testid={`match-score-${m.startup_id}`}>
                  {m.match_score}
                </div>
              </div>
              <div className="text-xs text-ink-muted line-clamp-2 mb-2">
                {m.one_line_pitch || "No pitch line yet."}
              </div>
              <div className="text-xs text-emerald-700 font-heading opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                View deal <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
